/**
 * Resolve the *effective* site config for the current request.
 *
 * Own-brand (greenclaim / airrefund) sites get one build per domain, so the
 * statically imported `src/config.json` is always correct for them.
 *
 * The ~20 cobranding partners instead share a single build served from
 * `claim.claimservice.app`. There `src/config.json` is merely whichever
 * cobranding row happens to have `locale: en` (see `.docker/prepare.php`); the
 * partner that actually applies is carried in the `partner` query param and its
 * real config lives in `src/configs/<partner>.json`. This mirrors what
 * `getApiRoot` (src/services/api.tsx) and `useCoBrandingParams` already do at
 * runtime.
 *
 * Used by the old-funnel forwarding in `gatsby-browser.js`, which needs
 * `shouldRedirectToNewCalculator` / `newCalculatorDomain` from the *partner's*
 * config rather than the build default.
 */

import { parse } from 'qs';
import config from '../config.json';

/**
 * The subset of the site config this module cares about. Config rows come
 * straight from the backoffice site export and carry many more keys.
 */
export interface SiteConfig {
  type?: string;
  apiUrl?: string;
  shouldRedirectToNewCalculator?: boolean;
  newCalculatorDomain?: string;
}

/**
 * Partner slugs are site identifiers from the export ("gate1-nl", "tix-be").
 * The value reaches us from a query param and is interpolated into a dynamic
 * import path, so anything outside this shape is rejected outright.
 */
const PARTNER_PATTERN = /^[a-z0-9-]+$/i;

/**
 * Current partner, or `null` on own-brand sites.
 *
 * The query param wins because on a cold entry (bookmark, email link) the
 * session has not been primed yet; sessionStorage is the fallback for
 * client-side navigations, where `useCoBrandingParams` has already stored it.
 */
export const getPartner = (search = ''): string | null => {
  const param = parse(search, { ignoreQueryPrefix: true })?.partner;
  if (typeof param === 'string' && PARTNER_PATTERN.test(param)) {
    return param;
  }

  try {
    const stored = sessionStorage.getItem('partner');
    return stored && PARTNER_PATTERN.test(stored) ? stored : null;
  } catch (error) {
    // Private-mode / blocked storage.
    return null;
  }
};

/**
 * True when this build is the shared cobranding one.
 *
 * On that build `src/config.json` is not a neutral default — it is a real
 * partner row (whichever has `locale: en`). Falling back to it would forward a
 * partner-less visitor to *that* partner's domain, so the fallback there has to
 * be an empty config, which reads as "do not redirect".
 */
const buildConfig: SiteConfig = config;

export const isCobrandingBuild = buildConfig.type === 'cobranding';

const FALLBACK_CONFIG: SiteConfig = isCobrandingBuild ? {} : buildConfig;

/**
 * Load the config for `partner`, falling back per `FALLBACK_CONFIG`.
 *
 * Keep this a single, flat template literal. `getApiRoot` (src/services/api.tsx)
 * nests one template inside another, and webpack 4's context-module parser only
 * survives that because the nested part sits inside a ternary — written
 * unconditionally it emits unbalanced parens and the bundle fails to build.
 *
 * The resulting require-context is `src/configs/`, which exists in every
 * checkout (`src/configs/tix-be.json` is tracked) as well as in the cobranding
 * build, where `.docker/prepare.php` fills it with every partner row.
 */
export const loadEffectiveConfig = async (
  partner: string | null
): Promise<SiteConfig> => {
  if (!partner) {
    return FALLBACK_CONFIG;
  }

  try {
    const loaded = await import(`../configs/${partner}.json`);
    return loaded?.default || FALLBACK_CONFIG;
  } catch (error) {
    return FALLBACK_CONFIG;
  }
};
